import { BarElement, CategoryScale, Chart as ChartJS, LinearScale } from 'chart.js';
import { useMemo, type JSX } from 'react';
import { Bar } from 'react-chartjs-2';

import type { ResultDistributionType } from 'types/api/diagnosisReportRefResponseType';

import '../../assets/styles/ws-report.scss';
import { formatNumberToCurrency } from 'utils/app';
import { stackedBarSegmentLabelsPlugin } from './wsReportResultDistribution.plugin';
import {
  buildLegendItems,
  buildStackedBarData,
  buildStackedBarOptions,
  computeTotalCount,
} from './wsReportResultDistribution.utils';

ChartJS.register(BarElement, CategoryScale, LinearScale);
ChartJS.register(stackedBarSegmentLabelsPlugin);

/**
 * 診断結果の分布を表示するコンポーネント（帯グラフ＋タグ状凡例）
 */
export default function WsReportResultDistribution(props: {
  resultDistribution: ResultDistributionType[];
  title?: string;
  countUnit?: string;
}): JSX.Element {
  const cardTitle = props.title ?? '診断結果の分布';
  const unit = props.countUnit ?? '名';

  const totalCount = useMemo(
    () => computeTotalCount(props.resultDistribution),
    [props.resultDistribution]
  );

  const stackedBarData = useMemo(
    () => buildStackedBarData(props.resultDistribution, totalCount),
    [props.resultDistribution, totalCount]
  );

  const stackedBarOptions = useMemo(
    () => buildStackedBarOptions(props.resultDistribution, totalCount, unit),
    [props.resultDistribution, totalCount, unit]
  );

  const legendItems = buildLegendItems(props.resultDistribution, totalCount, unit);

  return (
    <section className="section-chart section-result-distribution">
      <div className="card card-chart">
        <div className="card-header">
          <h3 className="card-title">{cardTitle}</h3>
        </div>
        <div className="card-body pt-3">
          {totalCount > 0 ? (
            <div className="result-distribution-stacked-bar-wrap">
              <div className="result-distribution-stacked-bar-chart">
                <Bar data={stackedBarData} options={stackedBarOptions} />
              </div>
              <p className="result-distribution-stacked-bar-total text-end mb-0 mt-2">
                Total {formatNumberToCurrency(totalCount)}
                {unit}
              </p>
              <div className="result-distribution-stacked-bar-legend">
                <ul className="list-unstyled mb-0 mt-3">
                  {legendItems.map((item, index) => (
                    <li
                      key={`stacked-legend-${index}`}
                      className="result-distribution-stacked-bar-legend-item"
                    >
                      <span
                        className="result-distribution-stacked-bar-legend-dot"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="result-distribution-stacked-bar-legend-label">
                        {item.title}
                      </span>
                      <span className="result-distribution-stacked-bar-legend-value">
                        {item.valueLabel}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center p-4">
              <p>データがありません</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
